import React from 'react';
import { FlowRun } from './types';
import { Column } from 'new/elements/Table/types';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { FlowRunsActionsMenu } from '../FlowRunsActionsMenu/FlowRunsActionsMenu';
import FormatUtils from 'js/lib/FormatUtils';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import { isNil } from 'lodash';
import { FlowRunStatus } from '../FlowRunStatus/FlowRunStatus';
import { FlowHealthBar, getFlowHealthSeverity } from 'new/elements/FlowHealthBar';
import DateUtils from 'js/lib/DateUtils';
import { IconButton, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ThreeDotMenu } from 'new/elements/ThreeDotMenu';
import { Section } from 'new/elements/ThreeDotMenu/ThreeDotMenu';
import { ResourceTriage } from 'new/components/monitor/ResourceTriage';

import s from './FlowRunsTable.module.scss';

const ResourceTriageIcon = React.lazy(() =>
  import('new/components/icons/fi-rr-stethoscope.svg').then((module) => ({
    default: module.ReactComponent,
  })),
);

const ResourceTriageLazyIcon = () => {
  return (
    <React.Suspense fallback={null}>
      <ResourceTriageIcon />
    </React.Suspense>
  );
};

const generateColumnsForFlowRunsTable = ({
  history,
  triageChatEnabled,
  onOpenTriage,
}: any = {}): Column<FlowRun>[] => {
  return [
    {
      width: 1,
      name: '',
      field: 'health',
      tdClassName: s.healthCell,
      renderRow: (row: FlowRun) => <FlowHealthBar metrics={row.metrics} />,
    },
    {
      width: 35,
      name: 'Flow Name',
      field: 'name',
      renderRow: (row: FlowRun) => (
        <ConnectionTypeChip
          icon={row?.icon}
          label={row?.name}
          resourceId={row?.origin_node_id?.toString()}
        />
      ),
    },
    {
      width: 15,
      name: 'Start Time',
      field: 'startTime',
      renderRow: (row: any) => {
        const last_run_id = row?.last_run_id;

        // 1. Handle null/undefined and non-numbers
        if (isNil(last_run_id) || typeof last_run_id !== 'number') {
          return '-';
        }

        // 2. Create Date object and check validity
        const lastRunDate = new Date(last_run_id);
        if (isNaN(lastRunDate.getTime())) {
          return '-';
        }

        // 3. Define the 24-hour threshold
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

        // 4. Compare and format
        if (last_run_id > twentyFourHoursAgo) {
          // Within last 24 hours - show relative time
          return DateUtils.showTimeSince(last_run_id);
        } else {
          // Older than 24 hours - show formatted date/time
          const formattedDate = DateUtils.convertDateStringToFormat(
            lastRunDate,
            'MMM D, YYYY [at] h:mm A z',
          );

          // Optional: Show relative time in tooltip for older dates
          const relativeTime = DateUtils.showTimeSince(last_run_id);
          return (
            <Tooltip title={relativeTime || ''}>
              <span>{formattedDate}</span>
            </Tooltip>
          );
        }
      },
    },
    {
      name: 'Run Status',
      field: 'status',
      renderRow: (row: FlowRun) => <FlowRunStatus row={row} />,
    },
    {
      name: 'Flow Records',
      field: 'records',
      center: true,
      renderRow: (row: any) => {
        const hideRecords = shouldHideRecordsForResource(row);

        if (hideRecords) {
          // check if we hide records for this flow type
          const size = row?.metrics?.size || row?.size;
          return size ? FormatUtils.bytesToString(size) : '-';
        }

        // show record count for other flows
        return row?.metrics?.records ? FormatUtils.formatNamedNumber(row?.metrics?.records) : '-';
      },
    },
    {
      name: 'Flow Errors',
      field: 'errors',
      center: true,
      renderRow: (row: any) => {
        const severity = getFlowHealthSeverity(row?.metrics);

        if (severity === 'unknown') return '-'; // metrics not yet resolved → unknown, render "-" (not a zero).

        const errors = Number(row?.metrics?.errors || 0);

        if (errors > 0) {
          return <span className={s.flowErrorsValue}>{FormatUtils.formatNamedNumber(errors)}</span>;
        }

        return <span className={s.flowErrorsZero}>0</span>; // resolved with no errors → muted zero.
      },
    },
    {
      name: '',
      field: '',
      renderRow: (row: FlowRun) => {
        const pathToResourceTriage = `${ResourceTriage.screenConfig.path}?type=flows&id=${row?.origin_node_id}`;
        const actions = [
          {
            actions: [
              {
                icon: <ResourceTriageLazyIcon />,
                title: 'Resource Triage',
                onClick: () => history?.push(pathToResourceTriage),
              },
            ],
          },
        ] as Section[];

        return (
          <div className={s.actionsMenu}>
            {triageChatEnabled && (
              <Tooltip title="Triage with AI">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onOpenTriage?.(row)}
                    data-testid="triage-chat-row-button"
                    aria-label="Triage with AI"
                  >
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Flow Insights">
              <span>
                <FlowRunsActionsMenu flowId={row?.origin_node_id} />
              </span>
            </Tooltip>
            <ThreeDotMenu actions={actions} />
          </div>
        );
      },
    },
  ];
};

export { generateColumnsForFlowRunsTable };
